import React, { useState, useEffect, useCallback } from "react";
import { Card, Row, Col, Typography, Flex, Button, DatePicker, Table, Tag, Tooltip, message, Spin } from "antd";
import { CreditCardOutlined, DownloadOutlined, ReloadOutlined, CheckCircleOutlined } from "@ant-design/icons";
import InrOutlined from "../../general/components/common/InrOutlined";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { getCookie } from "../../utils/cookieUtils";
import { getPaymentHistory, createRenewalOrder, verifyRenewalPayment } from "../../services/organizationService";

const { Title, Text } = Typography;

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const derivePlanInfo = (latest) => {
  if (!latest) return null;
  const from = latest.fromDate ? new Date(latest.fromDate) : new Date();
  const to = latest.toDate ? new Date(latest.toDate) : new Date();
  const diffDays = Math.max(0, Math.ceil((to - Date.now()) / (1000 * 60 * 60 * 24)));

  return {
    status: latest.status === "paid" ? "Active (Paid)" : latest.status || "Active",
    subscriptionFrom: from.toISOString().slice(0, 10),
    subscriptionTo: to.toISOString().slice(0, 10),
    teamSizeMax: Number(latest.teamSizeMax || latest.teamSize || 20),
    daysLeft: diffDays || Number(latest.days || 30),
    dailyRatePerPerson: Number(latest.pricePerPersonPerDay || 10),
    amount: Number(latest.amount || 0) / 100, // paise to INR
  };
};

function SubscriptionPayments() {
  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";

  const [payments, setPayments] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalRecords, setTotalRecords] = useState(0);
  const [renewDate, setRenewDate] = useState(null);
  const [renewLoading, setRenewLoading] = useState(false);

  // Plan info derived from live payments
  const [planInfo, setPlanInfo] = useState({
    status: "Active",
    subscriptionFrom: new Date().toISOString().slice(0, 10),
    subscriptionTo: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    teamSizeMax: 20,
    daysLeft: 30,
    dailyRatePerPerson: 10,
    amount: 0,
  });

  const loadHistory = useCallback(
    async (page = 1, size = pageSize) => {
      if (!effectiveCompanyCode) return;
      setTableLoading(true);
      try {
        const res = await getPaymentHistory(effectiveCompanyCode, { page, limit: size });
        if (res?.success && Array.isArray(res.payments)) {
          setPayments(res.payments);
          setTotalRecords(typeof res.total === "number" ? res.total : res.payments.length);
          if (page === 1 && res.payments[0]) {
            const derived = derivePlanInfo(res.payments[0]);
            if (derived) setPlanInfo(derived);
          }
        }
      } catch (err) {
        console.error("Failed to load payment history:", err);
      } finally {
        setTableLoading(false);
        setInitialLoading(false);
      }
    },
    [effectiveCompanyCode, pageSize],
  );

  useEffect(() => {
    loadHistory(1, pageSize);
  }, [loadHistory, pageSize]);

  const handleRenewSubscription = async () => {
    if (!renewDate) {
      message.warning("Please select a date to extend your subscription until.");
      return;
    }
    setRenewLoading(true);
    try {
      // 1. Ensure Razorpay Checkout SDK is loaded
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded || !window.Razorpay) {
        message.error("Could not load Razorpay gateway. Please check your network connection.");
        setRenewLoading(false);
        return;
      }

      // 2. Call backend to create renewal order
      const orderRes = await createRenewalOrder(effectiveCompanyCode, renewDate.format("YYYY-MM-DD"));
      if (!orderRes?.success) {
        message.error(orderRes?.message || "Failed to create subscription renewal order.");
        setRenewLoading(false);
        return;
      }

      // 3. Configure Razorpay modal options
      const options = {
        key: orderRes.keyId || "rzp_test_SQgiFp0lxgOCjs",
        amount: orderRes.amount,
        currency: orderRes.currency || "INR",
        name: "DealVoice",
        description: `Subscription extension until ${renewDate.format("DD MMM YYYY")}`,
        prefill: {
          name: user?.name || orderRes.companyName || "Administrator",
          email: user?.email || orderRes.email || "admin@company.com",
          contact: user?.phone || user?.phoneNumber || orderRes.mobile || "9999999999",
        },
        theme: {
          color: "#2459d3",
        },
        handler: async function (response) {
          message.loading({ content: "Verifying payment with gateway...", key: "pay_verify" });
          try {
            const verifyRes = await verifyRenewalPayment({
              companyCode: effectiveCompanyCode,
              razorpay_order_id: response.razorpay_order_id || orderRes.orderId,
              razorpay_payment_id: response.razorpay_payment_id || `pay_${Date.now()}`,
              razorpay_signature: response.razorpay_signature || "test_signature",
            });
            if (verifyRes?.success) {
              message.success({ content: "Payment confirmed! Plan extended successfully.", key: "pay_verify" });
              setRenewDate(null);
              loadHistory();
            } else {
              message.error({ content: verifyRes?.message || "Payment verification failed.", key: "pay_verify" });
            }
          } catch (err) {
            console.error("Verification error:", err);
            message.error({ content: "Payment verification failed.", key: "pay_verify" });
          }
        },
        modal: {
          ondismiss: function () {
            message.info("Payment window closed.");
          },
        },
      };

      // Only pass order_id if it was created on live Razorpay servers
      if (orderRes.orderId && !String(orderRes.orderId).startsWith("order_mock_")) {
        options.order_id = orderRes.orderId;
      }

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (resp) {
        console.error("Razorpay Payment Failed:", resp.error);
        message.error(resp.error?.description || "Payment failed at gateway.");
      });
      rzp.open();
    } catch (err) {
      console.error("Renewal payment error:", err);
      message.error(err.message || "An error occurred while opening payment gateway.");
    } finally {
      setRenewLoading(false);
    }
  };

  const columns = [
    {
      title: "Invoice / Order",
      key: "order",
      render: (_, r) => (
        <Flex vertical>
          <Text strong style={{ color: "#0f172a", fontSize: "0.85rem" }}>
            {r.razorpayPaymentId || r.razorpayOrderId || r._id}
          </Text>
          <Text type="secondary" style={{ fontSize: "0.72rem" }}>
            {r.paymentMethod ? `via ${r.paymentMethod.toUpperCase()}` : "Online"}
          </Text>
        </Flex>
      ),
    },
    {
      title: "Billing Period",
      key: "period",
      render: (_, r) => {
        const from = r.fromDate ? new Date(r.fromDate).toLocaleDateString("en-IN", { month: "short", day: "2-digit", year: "2-digit" }) : "—";
        const to = r.toDate ? new Date(r.toDate).toLocaleDateString("en-IN", { month: "short", day: "2-digit", year: "2-digit" }) : "—";
        return (
          <Text style={{ fontSize: "0.82rem" }}>
            {from} &rarr; {to}
          </Text>
        );
      },
    },
    {
      title: "Seats",
      dataIndex: "teamSizeMax",
      key: "seats",
      align: "center",
      render: (s, r) => <Tag color="blue">{s || r.teamSize || 20} seats</Tag>,
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      render: (_, r) => {
        const rawAmt = Number(r.amount || 0);
        const amtInRupees = Math.abs(rawAmt > 10000 ? rawAmt / 100 : rawAmt);
        return (
          <Text strong style={{ color: "#0f172a", fontSize: "0.88rem" }}>
            ₹{amtInRupees.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (st) => (
        <Tag color={st === "paid" ? "green" : "orange"} style={{ borderRadius: 6, textTransform: "capitalize" }}>
          {st || "Paid"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      render: (_, r) => (
        <Tooltip title="Download Receipt">
          <Button type="text" size="small" icon={<DownloadOutlined style={{ color: "#2459d3" }} />} onClick={() => message.info(`Downloading subscription receipt for ${r._id}...`)} />
        </Tooltip>
      ),
    },
  ];

  return (
    <Spin spinning={initialLoading}>
      <Flex vertical gap={16}>
        {/* ── CARD 1: CURRENT ACTIVE SUBSCRIPTION PLAN ── */}
        <Card className="org-card" styles={{ body: { padding: 20 } }}>
          <Flex vertical gap={16}>
            <Flex justify="space-between" align="center">
              <Flex align="center" gap={8}>
                <CreditCardOutlined style={{ color: "#2459d3", fontSize: 18 }} />
                <Title level={5} style={{ margin: 0, color: "#1e293b", fontSize: "0.95rem" }}>
                  Current Subscription Plan
                </Title>
              </Flex>
              <Tag color="success" style={{ borderRadius: 10, fontWeight: 600 }}>
                <CheckCircleOutlined /> {planInfo.status}
              </Tag>
            </Flex>

            <Row gutter={[16, 12]}>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Valid From
                </Text>
                <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "0.85rem" }}>
                  {new Date(planInfo.subscriptionFrom).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Valid Until
                </Text>
                <div style={{ fontWeight: 600, color: "#2459d3", fontSize: "0.85rem" }}>
                  {new Date(planInfo.subscriptionTo).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              </Col>

              <Col span={12}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Active Allocated Seats
                </Text>
                <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem" }}>{planInfo.teamSizeMax} Reps</div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Remaining Period
                </Text>
                <div style={{ fontWeight: 700, color: "#10b981", fontSize: "0.9rem" }}>{planInfo.daysLeft} Days</div>
              </Col>
            </Row>

            {/* Extend Subscription */}
            <div
              style={{
                marginTop: 4,
                padding: "12px 16px",
                background: "#f8fafc",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
              }}
            >
              <Text strong style={{ fontSize: "0.82rem", color: "#1e293b" }}>
                Extend or Renew Plan
              </Text>
              <Flex gap={8} align="center" style={{ marginTop: 8 }} wrap="wrap">
                <DatePicker placeholder="Select Extension Date" value={renewDate} onChange={setRenewDate} disabledDate={(current) => current && current <= (planInfo.subscriptionTo ? dayjs(planInfo.subscriptionTo) : dayjs().endOf("day"))} size="middle" style={{ width: 220 }} />
                <Button type="primary" icon={<InrOutlined />} loading={renewLoading} onClick={handleRenewSubscription}>
                  Pay & Extend
                </Button>
              </Flex>
            </div>
          </Flex>
        </Card>

        {/* ── CARD 2: PAYMENT HISTORY ── */}
        <Card className="org-card" styles={{ body: { padding: 20 } }}>
          <Flex vertical gap={14}>
            <Flex justify="space-between" align="center">
              <Title level={5} style={{ margin: 0, color: "#1e293b", fontSize: "0.95rem" }}>
                Payment & Billing History ({totalRecords})
              </Title>
              <Button type="text" size="small" icon={<ReloadOutlined />} onClick={() => loadHistory(currentPage, pageSize)} title="Refresh history" />
            </Flex>

            <Table
              columns={columns}
              dataSource={payments}
              loading={tableLoading}
              rowKey={(r) => r._id || r.razorpayPaymentId || r.razorpayOrderId || `${r.fromDate}_${r.toDate}`}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalRecords,
                showSizeChanger: true,
                pageSizeOptions: ["5", "10", "20", "50", "100"],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                size: "default",
                onChange: (page, newPageSize) => {
                  setCurrentPage(page);
                  setPageSize(newPageSize);
                  loadHistory(page, newPageSize);
                },
              }}
              size="middle"
              className="org-table"
              scroll={{ x: 750 }}
            />
          </Flex>
        </Card>
      </Flex>
    </Spin>
  );
}

export default SubscriptionPayments;
